import { useTranslation } from "react-i18next";
import { convertToPST } from "../../../Helper/FormatDatetimes";
import { useState } from "react";
import instance from "../../../Interceptor/api_instance";
import { TableNavigation } from "../../../Components/Tables/Vehicles/TableNavigation";
import {
  Spinner,
  TablePlaceHolder,
} from "../../../Components/LoadingAnimations/LoadingAnimations";
import { SpinnerOverlay } from "../../../Components/LoadingAnimations/SpinnerOverlay";
import { useAuth } from "../../../Auth/AuthProvider";
import { useNavigate } from "react-router-dom";
import { usePaginatedQuery } from "../../../Hooks/usePaginatedQuery";
import { buildParams } from "../../../Components/Utility/URLUtils";
import { useApiMutation } from "../../../Hooks/useApiMutation";
import TableImgModal from "../../../Components/Modals/TableImgModal";

const DEFAULT_URL = "assets/ctpat-reports/?";

export const F12Table = () => {
  const { t } = useTranslation();
  const { yardsData } = useAuth();

  const navigate = useNavigate();

  // Find the default yard ID for Mexico
  const defaultYardId = yardsData?.find(
    (yard) => yard.loc_country === "Mexico"
  ).id;

  const [filters, setFilters] = useState({
    location: defaultYardId,
    start_date: null,
    end_date: null,
    category: "Visitor",
  });
  const [selectedRow, setSelectedRow] = useState(null);

  // Fetch paginated data based on filters, with loading and error states
  const { state, dispatch, setPage } = usePaginatedQuery(
    `${DEFAULT_URL}${buildParams(filters)}`
  );

  // Mutation for exporting data, on success navigate to export jobs page
  const {
    mutate: exportData,
    isLoading,
    error,
  } = useApiMutation(async ({ signal }) => {
    const response = await instance.post("/files/export-ctpat/f12/", filters, {
      signal,
    });
    if (response.status === 201) {
      navigate("/export-jobs");
    }
  });

  const headers = [
    t("forms.type.newDriver.input-headers.firstName"),
    t("forms.type.f12.company"),
    t("forms.type.f12.person-visiting"),
    t("yard-tables.table-columns.vehicle-plates"),
    t("forms.type.f12.id-num"),
    t("forms.type.f12.badge-num"),
    t("yard-tables.table-columns.arrival-time"),
    t("yard-tables.table-columns.check-out"),
    t("yard-tables.table-columns.guard-check-in"),
    t("yard-tables.table-columns.guard-check-out"),
  ];

  // Handle filter changes by updating state and refetching data with new filters
  const handleFilter = (e) => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(`${DEFAULT_URL}${buildParams(newFilters)}`);
  };

  return (
    <>
      {state.isLoading && <SpinnerOverlay />}
      {error && <p className="error">{error}</p>}
      {selectedRow && (
        <TableImgModal
          close={() => setSelectedRow(null)}
          english={t}
          trailer={selectedRow}
        />
      )}
      <div className="search-filters-cont">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div className="search-filters">
            <div style={{ display: "flex", gap: "12px" }}>
              {/* Start date filter */}
              <label className="">
                {t("general.startDate")}{" "}
                <input
                  type="date"
                  onChange={handleFilter}
                  name="start_date"
                  value={filters.start_date}
                  className="text-input-sm"
                />
              </label>

              {/* End date filter */}
              <label className="">
                {t("general.endDate")}{" "}
                <input
                  type="date"
                  onChange={handleFilter}
                  name="end_date"
                  value={filters.end_date}
                  className="text-input-sm"
                />
              </label>

              {/* Location filter */}
              <select
                className="text-input-sm"
                value={filters.location}
                onChange={handleFilter}
                name="location"
              >
                {yardsData
                  ?.filter((yard) => yard.loc_country == "Mexico")
                  .map((yard) => (
                    <option key={yard.id} value={yard.id}>
                      {yard.loc_name}
                    </option>
                  ))}
              </select>
              {isLoading ? (
                <Spinner />
              ) : (
                <button
                  className="change-pwd-btn all-btn"
                  type="button"
                  onClick={exportData}
                >
                  {t("buttons.export")}
                </button>
              )}
            </div>
          </div>
          <TableNavigation state={state} dispatch={dispatch} />
        </div>
      </div>

      {/* Data table */}
      {state.isLoading ? (
        <TablePlaceHolder />
      ) : (
        <div id="forwardTable" className="table-cont">
          <table className="yard-table">
            <thead>
              <tr className="table-header">
                {headers?.map((item, index) => (
                  <th key={index} scope="col">
                    <div>{item}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {state.data?.map((item, index) => (
                <tr
                  key={index}
                  className="data-rows row-hover gray"
                  data-testid={`row-${item.id}`}
                  onClick={() => setSelectedRow(item)}
                >
                  <td
                    data-label="driver"
                    className="gray"
                    key={`${index}-driver`}
                  >
                    {item.full_name}
                  </td>
                  <td
                    data-label="company"
                    className="gray"
                    key={`${index}-company`}
                  >
                    {item.company}
                  </td>
                  <td
                    data-label="person-visiting"
                    className="gray"
                    key={`${index}-person-visiting`}
                  >
                    {item.person_visiting ? item.person_visiting : "N/A"}
                  </td>
                  <td
                    data-label="vehicle-plates"
                    className="gray"
                    key={`${index}-vehicle-plates`}
                  >
                    {item.license_plate ? item.license_plate : "N/A"}
                  </td>
                  <td
                    data-label="id-num"
                    className="gray"
                    key={`${index}-id-num`}
                  >
                    {item.id_num ? item.id_num : "N/A"}
                  </td>
                  <td
                    data-label="badge-num"
                    className="gray"
                    key={`${index}-badge-num`}
                  >
                    {item.badge_num ? item.badge_num : "N/A"}
                  </td>
                  <td data-label="date" className="gray" key={`${index}-date`}>
                    {convertToPST(item.check_in_dt)}
                  </td>
                  <td
                    data-label="checkout"
                    className="gray"
                    key={`${index}-checkout`}
                  >
                    {item.check_out_dt
                      ? convertToPST(item.check_out_dt)
                      : "N/A"}
                  </td>
                  <td
                    data-label="guard-in"
                    className="gray"
                    key={`${index}-guard-in`}
                  >
                    {item.entry_guard}
                  </td>
                  <td
                    data-label="guard-out"
                    className="gray"
                    key={`${index}-guard-out`}
                  >
                    {item.checkout_guard ? item.checkout_guard : "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};
